/** @format */

import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { PageTypeEnum } from '@category/data-access';
import { EMPTY, Observable, forkJoin, of } from 'rxjs';
import { HomrDataService, HomrResponse } from '@shared/homr/data-access';
import { HttpService, Logger, WINDOW } from '@shared/data-access/services';
import { Injectable, PLATFORM_ID, REQUEST_CONTEXT, inject } from '@angular/core';
import { catchError, switchMap } from 'rxjs/operators';

import { Chain, ProductListSEOResponse } from '@shared/data-access/interfaces';
import { ProTipsDataService } from '@shared/components';
import { SeoService } from '@seo/data-access';
import { isPlatformBrowser } from '@angular/common';

// eslint-disable-next-line @angular-eslint/use-injectable-provided-in
@Injectable()
export class CategoryResolveGuard {
  private readonly logger = inject(Logger);
  private readonly homrService = inject(HomrDataService);
  private readonly seoService = inject(SeoService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly httpService = inject(HttpService);
  private readonly proTipsDataService = inject(ProTipsDataService);
  private readonly chain = inject(Chain);
  private readonly window = inject(WINDOW);
  private readonly propagateToServer = inject<{
    PROPAGATE_ERROR_TO_SERVER: (propagatedError: { errorType: string; redirectUrl?: string }) => {
      errorType: string;
      redirectUrl?: string;
    };
  }>(REQUEST_CONTEXT, { optional: true })?.PROPAGATE_ERROR_TO_SERVER;

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const seoToken = route.params['seoToken'];
    this.homrService.setPageName(seoToken);

    const homrCallWithFallback = this.homrService.getData$(`category`, 2).pipe(
      catchError((error) => {
        this.logger.error('Error getting homr data in category container', error);

        return this.homrService.getDefault$(`category`);
      }),
    );

    return forkJoin({
      seoData: this.seoService.getSEOData$(seoToken, 1, false, PageTypeEnum.CATEGORY),
      homrData: homrCallWithFallback,
    }).pipe(
      switchMap(
        ({ seoData, homrData }: { seoData: ProductListSEOResponse; homrData: HomrResponse }) => {
          const categoryData = seoData?.categories?.[0];

          if (categoryData) {
            const { pageType, seoUrl, redirect } = categoryData;
            const redirectResponse = this.handleCategoryRedirect(pageType, seoUrl, redirect);

            if (redirectResponse) {
              return redirectResponse;
            }

            // initialize SEO
            this.seoService.initializePageSEO(seoData, state.url.split('?')[0]);

            // Fire the pro-tips request early on the server as soon as the SEO url is known,
            // so it overlaps SSR rendering instead of being triggered later at component init.
            // Angular's HTTP transfer cache carries the response to the browser.
            if (!isPlatformBrowser(this.platformId) && seoUrl) {
              this.proTipsDataService
                .getProTipsData$(seoUrl, this.chain.chainIdentifierAbbr)
                .pipe(
                  catchError((err) => {
                    this.logger.error('Pro-tips prefetch failed', err);
                    return of([]);
                  }),
                )
                .subscribe();
            }
          }

          return of({ seoData, homrData });
        },
      ),
      catchError((error) => {
        if (
          !isPlatformBrowser(this.platformId) &&
          error instanceof Error &&
          error.message === 'Category Redirect Error'
        ) {
          return EMPTY;
        }

        // Log the error
        this.logger.error(
          `APPLICATION_ERROR:HTTP_ERROR: Error getting page by SEO TOKEN: ${
            seoToken
          }. ${this.getErrorToPrint(error)}`,
        );

        return of(null);
      }),
    );
  }

  private handleCategoryRedirect(
    pageType: string,
    seoUrl: string,
    shouldRedirectCategory: boolean,
  ): Observable<never> | null {
    // Will cause a redirect if family page or static page is requested
    // from the category app  ex: /c/baseball-gloves which is family page
    const shouldRedirect =
      (pageType === PageTypeEnum.CATEGORY && shouldRedirectCategory) ||
      pageType === PageTypeEnum.FAMILY ||
      pageType === PageTypeEnum.STATIC;

    if (!shouldRedirect) {
      return null;
    }

    const redirectUrl = `/${pageType}/${seoUrl}`;

    if (isPlatformBrowser(this.platformId)) {
      this.window.location.replace(redirectUrl);
      return EMPTY;
    }

    if (!this.propagateToServer) {
      this.httpService.redirect(redirectUrl);
      return EMPTY;
    }

    this.propagateToServer({
      errorType: 'redirect',
      redirectUrl,
    });
    throw new Error('Category Redirect Error', {
      cause: JSON.stringify({ errorType: 'redirect', redirectUrl }),
    });
  }

  /**
   * @description
   * Accepts an error and stringifies the important parts
   */
  private getErrorToPrint(error: unknown): string {
    try {
      return JSON.stringify(error, ['status', 'message', 'arguments', 'type', 'name']);
    } catch {
      return '';
    }
  }
}
